import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getRandomSoalDeret, getRandomSoalAritmatika, getRandomSoalHitungCepat, soalDeretAngka, soalAritmatika, soalHitungCepat } from './bankSoalLogika';
import { catatHasilTes } from './progressTracker';
import { useCegahKeluarTes } from './cegahKeluarTes';
import { bacaProgresTes, hapusProgresTes, useAutosaveTes } from './useResumeTes';

// Gabungan semua bank soal (dipakai untuk merekonstruksi soalList persis sama
// saat resume, berdasar ID yang tersimpan — bukan generate ulang/acak baru).
const SEMUA_BANK_LOGIKA = [...soalDeretAngka, ...soalAritmatika, ...soalHitungCepat];
function soalDariMeta(meta) {
  const byId = {};
  SEMUA_BANK_LOGIKA.forEach(s => { byId[s.id] = s; });
  return (meta || []).map(m => (byId[m.id] ? { ...byId[m.id], jenis: m.jenis } : null)).filter(Boolean);
}

// ─── Konstanta ───────────────────────────────────────────────
const TOTAL_SOAL  = 30;
const WAKTU_MENIT = 25;
const WAKTU_TOTAL = WAKTU_MENIT * 60;

// ─── Helper ──────────────────────────────────────────────────
function formatWaktu(detik) {
  const m = Math.floor(detik / 60).toString().padStart(2, '0');
  const s = (detik % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

function hitungSkor(soalList, jawabanUser) {
  let benar = 0, salah = 0, kosong = 0;
  soalList.forEach(soal => {
    const userJawab = jawabanUser.find(j => j.id === soal.id);
    const jwb = userJawab?.jawaban;
    if (jwb === null || jwb === undefined || jwb === '') {
      kosong++;
    } else {
      const cocok = (soal.jenis === 'deret' || soal.jenis === 'hitung_cepat')
        ? Number(jwb) === Number(soal.jawaban)
        : jwb === soal.jawaban;
      if (cocok) benar++; else salah++;
    }
  });
  const skor = Math.round((benar / soalList.length) * 100);
  return { benar, salah, kosong, skor, total: soalList.length };
}

function getBadge(skor) {
  if (skor >= 90) return { label: 'Luar Biasa', color: 'text-green-700 bg-green-100 border-green-300' };
  if (skor >= 75) return { label: 'Baik', color: 'text-blue-700 bg-blue-100 border-blue-300' };
  if (skor >= 60) return { label: 'Cukup', color: 'text-yellow-700 bg-yellow-100 border-yellow-300' };
  return { label: 'Perlu Latihan', color: 'text-red-700 bg-red-100 border-red-300' };
}

// Banner "lanjutkan pengerjaan sebelumnya" — dipakai di kedua varian layar
// intro (tryout terkunci maupun pilih mode bebas).
function BannerLanjutkan({ progresTersimpan, onLanjutkan, onMulaiBaru }) {
  if (!progresTersimpan) return null;
  const labelMode = progresTersimpan.mode === 'deret' ? 'Deret Angka'
    : progresTersimpan.mode === 'aritmatika' ? 'Aritmatika'
    : progresTersimpan.mode === 'hitung_cepat' ? 'Hitung Cepat'
    : 'Campuran';
  const totalSoal = progresTersimpan.soalMeta?.length || 0;
  return (
    <div className="rounded-xl p-4 mb-5 border-2 border-blue-400 bg-blue-50">
      <p className="text-xs font-bold uppercase tracking-wide text-blue-700 mb-1">⏸️ Ada Pengerjaan Sebelumnya</p>
      <p className="text-sm text-blue-800 mb-3">
        Kamu belum menyelesaikan tes ini — mode <b>{labelMode}</b>, baru sampai soal <b>{(progresTersimpan.indeks || 0) + 1}/{totalSoal}</b>, sisa waktu <b>{formatWaktu(progresTersimpan.waktuSisa || 0)}</b>.
      </p>
      <div className="flex gap-2">
        <button onClick={onLanjutkan} className="flex-1 py-2 rounded-lg bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 transition">
          Lanjutkan
        </button>
        <button onClick={onMulaiBaru} className="flex-1 py-2 rounded-lg border border-blue-300 text-blue-600 text-sm font-semibold hover:bg-blue-100 transition">
          Mulai Baru
        </button>
      </div>
    </div>
  );
}

// ─── Layar Intro dengan Pilih Mode ───────────────────────────
function LayarIntro({ onMulai, onBack, tryoutMode, progresTersimpan, onLanjutkan, onMulaiBaru }) {
  const [mode, setMode] = useState(null);

  const MODES = [
    {
      id: 'deret',
      icon: '🔢',
      label: 'Deret Angka',
      desc: '30 soal pola angka — ketik jawaban',
      detail: 'Latihan fokus kemampuan penalaran pola bilangan',
      border: 'border-purple-400',
      bg: 'bg-purple-50',
      iconBg: 'bg-purple-100',
      textColor: 'text-purple-700',
      checkColor: 'text-purple-600',
    },
    {
      id: 'aritmatika',
      icon: '📖',
      label: 'Aritmatika',
      desc: '30 soal cerita & hitungan — pilihan ganda',
      detail: 'Latihan fokus kemampuan berhitung dan logika cerita',
      border: 'border-green-400',
      bg: 'bg-green-50',
      iconBg: 'bg-green-100',
      textColor: 'text-green-700',
      checkColor: 'text-green-600',
    },
    {
      id: 'campuran',
      icon: '🧮',
      label: 'Campuran',
      desc: '15 deret angka + 15 aritmatika',
      detail: 'Skor Deret & Aritmatika ditampilkan terpisah',
      border: 'border-blue-400',
      bg: 'bg-blue-50',
      iconBg: 'bg-blue-100',
      textColor: 'text-blue-700',
      checkColor: 'text-blue-600',
    },
    {
      id: 'hitung_cepat',
      icon: '⚡',
      label: 'Hitung Cepat',
      desc: '30 soal aritmatika langsung — ketik jawaban',
      detail: 'Modul baru: fokus kecepatan & ketelitian berhitung, belum masuk skor Tryout resmi',
      border: 'border-amber-400',
      bg: 'bg-amber-50',
      iconBg: 'bg-amber-100',
      textColor: 'text-amber-700',
      checkColor: 'text-amber-600',
    },
  ];

  const selected = MODES.find(m => m.id === mode);
  const modeCampuran = MODES.find(m => m.id === 'campuran');

  // Dalam konteks Tryout resmi, mode dikunci ke Campuran — semua peserta
  // mengerjakan komposisi soal yang sama. Notice besar biar jelas kenapa
  // tidak ada pilihan mode.
  if (tryoutMode) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg max-w-lg w-full p-6">
          <div className="text-center mb-5">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-3xl">🧮</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-800">Logika & Numerik</h1>
            <p className="text-gray-400 text-sm mt-1">Bagian dari Tryout Resmi</p>
          </div>

          <BannerLanjutkan progresTersimpan={progresTersimpan} onLanjutkan={onLanjutkan} onMulaiBaru={onMulaiBaru} />

          <div className="rounded-xl p-4 mb-4 border-2 border-amber-400 bg-amber-50">
            <p className="text-xs font-bold uppercase tracking-wide text-amber-700 mb-1">📌 Perhatian</p>
            <p className="text-sm font-semibold text-amber-800 leading-relaxed">
              Karena ini bagian dari Tryout resmi, mode dikunci otomatis ke <u>Campuran</u> — semua peserta mengerjakan komposisi soal yang sama, tidak bisa memilih mode lain.
            </p>
          </div>

          <div className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 ${modeCampuran.border} ${modeCampuran.bg} mb-5`}>
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${modeCampuran.iconBg}`}>
              <span className="text-2xl">{modeCampuran.icon}</span>
            </div>
            <div className="flex-1">
              <p className={`font-bold text-sm ${modeCampuran.textColor}`}>{modeCampuran.label}</p>
              <p className="text-xs text-gray-500">{modeCampuran.desc}</p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-4 mb-5 grid grid-cols-3 gap-3 text-center">
            <div><p className="text-lg font-bold text-gray-800">30</p><p className="text-xs text-gray-500">Soal</p></div>
            <div><p className="text-lg font-bold text-gray-800">25'</p><p className="text-xs text-gray-500">Waktu</p></div>
            <div><p className="text-lg font-bold text-gray-800">~50"</p><p className="text-xs text-gray-500">Per soal</p></div>
          </div>

          <button onClick={() => onMulai('campuran')}
            className="w-full py-3 rounded-xl font-bold border-2 border-blue-400 bg-blue-50 text-blue-700 hover:opacity-90 transition">
            Mulai Sekarang →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg max-w-lg w-full p-6">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <span className="text-3xl">🧮</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Logika & Numerik</h1>
          <p className="text-gray-400 text-sm mt-1">Pilih mode latihan</p>
        </div>

        <BannerLanjutkan progresTersimpan={progresTersimpan} onLanjutkan={onLanjutkan} onMulaiBaru={onMulaiBaru} />

        {/* Pilih Mode */}
        <div className="space-y-3 mb-5">
          {MODES.map(m => (
            <button
              key={m.id}
              onClick={() => setMode(m.id)}
              className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition text-left
                ${mode === m.id ? `${m.border} ${m.bg}` : 'border-gray-200 bg-white hover:border-gray-300'}`}
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0
                ${mode === m.id ? m.iconBg : 'bg-gray-100'}`}>
                <span className="text-2xl">{m.icon}</span>
              </div>
              <div className="flex-1">
                <p className={`font-bold text-sm ${mode === m.id ? m.textColor : 'text-gray-700'}`}>
                  {m.label}
                </p>
                <p className="text-xs text-gray-500">{m.desc}</p>
                {mode === m.id && (
                  <p className={`text-xs mt-1 font-medium ${m.textColor}`}>✦ {m.detail}</p>
                )}
              </div>
              {mode === m.id && (
                <span className={`text-xl font-bold ${m.checkColor}`}>✓</span>
              )}
            </button>
          ))}
        </div>

        {/* Info */}
        <div className="bg-gray-50 rounded-xl p-4 mb-5 grid grid-cols-3 gap-3 text-center">
          <div>
            <p className="text-lg font-bold text-gray-800">30</p>
            <p className="text-xs text-gray-500">Soal</p>
          </div>
          <div>
            <p className="text-lg font-bold text-gray-800">25'</p>
            <p className="text-xs text-gray-500">Waktu</p>
          </div>
          <div>
            <p className="text-lg font-bold text-gray-800">~50"</p>
            <p className="text-xs text-gray-500">Per soal</p>
          </div>
        </div>

        {/* Tombol */}
        <div className="flex gap-3">
          <button
            onClick={onBack}
            className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-600 font-semibold hover:bg-gray-50 transition"
          >
            ← Kembali
          </button>
          <button
            onClick={() => mode && onMulai(mode)}
            disabled={!mode}
            className={`flex-grow py-3 rounded-xl font-bold transition
              ${mode
                ? `${selected?.border} border-2 ${selected?.bg} ${selected?.textColor} hover:opacity-90`
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
          >
            {mode ? `Mulai ${selected?.label} →` : 'Pilih mode dulu'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Soal Deret ───────────────────────────────────────────────
function SoalDeret({ soal, onJawab, jawaban }) {
  const inputRef = useRef(null);
  useEffect(() => { if (inputRef.current) inputRef.current.focus(); }, [soal.id]);

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <span className="bg-purple-100 text-purple-700 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
          🔢 Deret Angka
        </span>
        <span className="text-xs text-gray-400">Lanjutkan pola berikut</span>
      </div>
      <div className="bg-purple-50 border border-purple-100 rounded-xl p-5 mb-5 text-center">
        <p className="text-2xl font-bold text-gray-800 tracking-wider">{soal.deret}</p>
        <p className="text-gray-400 text-xs mt-2">Pola: {soal.pola}</p>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-gray-500 text-sm font-medium">Jawaban:</span>
        <input
          ref={inputRef}
          type="number"
          className="border-2 border-purple-300 rounded-lg px-4 py-2 text-lg font-bold w-40 text-center bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100"
          placeholder="..."
          value={jawaban ?? ''}
          onChange={e => onJawab(e.target.value)}
        />
      </div>
    </div>
  );
}

// ─── Soal Hitung Cepat ──────────────────────────────────────────
function SoalHitungCepat({ soal, onJawab, jawaban }) {
  const inputRef = useRef(null);
  useEffect(() => { if (inputRef.current) inputRef.current.focus(); }, [soal.id]);

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <span className="bg-amber-100 text-amber-700 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
          ⚡ Hitung Cepat
        </span>
        <span className="text-xs text-gray-400">Hitung hasilnya secepat mungkin</span>
      </div>
      <div className="bg-amber-50 border border-amber-100 rounded-xl p-5 mb-5 text-center">
        <p className="text-2xl font-bold text-gray-800 tracking-wider">{soal.ekspresi} = ...</p>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-gray-500 text-sm font-medium">Jawaban:</span>
        <input
          ref={inputRef}
          type="number"
          className="border-2 border-amber-300 rounded-lg px-4 py-2 text-lg font-bold w-40 text-center bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100"
          placeholder="..."
          value={jawaban ?? ''}
          onChange={e => onJawab(e.target.value)}
        />
      </div>
    </div>
  );
}

// ─── Soal Aritmatika ──────────────────────────────────────────
function SoalAritmatika({ soal, onJawab, jawaban }) {
  const huruf = ['a', 'b', 'c', 'd', 'e'];
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <span className="bg-green-100 text-green-700 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
          📖 Aritmatika
        </span>
        <span className="text-xs text-gray-400">Pilih jawaban yang tepat</span>
      </div>
      <div className="bg-green-50 border border-green-100 rounded-xl p-4 mb-4">
        <p className="text-base font-semibold text-gray-800 leading-relaxed">{soal.soal}</p>
      </div>
      <div className="space-y-2">
        {huruf.map(h => (
          (h in soal.pilihan) && (
            <button
              key={h}
              onClick={() => onJawab(h)}
              className={`w-full text-left px-4 py-3 rounded-xl border-2 font-medium transition text-sm
                ${jawaban === h
                  ? 'border-green-500 bg-green-100 text-green-800'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-green-300 hover:bg-green-50'}`}
            >
              <span className="font-bold mr-2 uppercase">{h}.</span>
              {soal.pilihan[h]}
            </button>
          )
        ))}
      </div>
    </div>
  );
}

// ─── Kartu Skor ───────────────────────────────────────────────
function KartuSkor({ label, icon, hasil, color }) {
  const badge = getBadge(hasil.skor);
  return (
    <div className={`rounded-xl p-4 border-2 ${color}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wide">{icon} {label}</p>
          <p className="text-3xl font-extrabold text-gray-800 mt-1">{hasil.skor}</p>
          <p className="text-xs text-gray-400">dari 100 poin</p>
        </div>
        <span className={`text-xs font-bold px-3 py-1 rounded-full border ${badge.color}`}>
          {badge.label}
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
        <div
          className="h-2 rounded-full transition-all duration-1000 bg-blue-500"
          style={{ width: `${hasil.skor}%` }}
        />
      </div>
      <div className="grid grid-cols-3 gap-2 text-center text-xs">
        <div className="bg-green-50 rounded-lg py-2">
          <p className="font-bold text-green-700 text-base">{hasil.benar}</p>
          <p className="text-gray-500">Benar</p>
        </div>
        <div className="bg-red-50 rounded-lg py-2">
          <p className="font-bold text-red-700 text-base">{hasil.salah}</p>
          <p className="text-gray-500">Salah</p>
        </div>
        <div className="bg-gray-50 rounded-lg py-2">
          <p className="font-bold text-gray-600 text-base">{hasil.kosong}</p>
          <p className="text-gray-500">Kosong</p>
        </div>
      </div>
    </div>
  );
}

// ─── Layar Hasil ──────────────────────────────────────────────
function LayarHasil({ mode, soalList, jawabanUser, onUlang, onBack, tryoutMode, tryoutId }) {
  const [showPembahasan, setShowPembahasan] = useState(false);

  // Hitung skor terpisah
  const soalDeret = soalList.filter(s => s.jenis === 'deret');
  const soalAritmatika = soalList.filter(s => s.jenis === 'aritmatika');
  const soalHitungCepat = soalList.filter(s => s.jenis === 'hitung_cepat');

  const hasilDeret = soalDeret.length > 0 ? hitungSkor(soalDeret, jawabanUser) : null;
  const hasilAritmatika = soalAritmatika.length > 0 ? hitungSkor(soalAritmatika, jawabanUser) : null;
  const hasilHitungCepat = soalHitungCepat.length > 0 ? hitungSkor(soalHitungCepat, jawabanUser) : null;

  // Skor total (rata-rata jika campuran)
  const skorTotal = mode === 'campuran'
    ? Math.round((hasilDeret.skor + hasilAritmatika.skor) / 2)
    : mode === 'deret' ? hasilDeret.skor
    : mode === 'hitung_cepat' ? hasilHitungCepat.skor
    : hasilAritmatika.skor;

  const badgeTotal = getBadge(skorTotal);

  // Gabungkan rincian benar/salah/kosong dari Deret & Aritmatika (kalau campuran)
  // atau ambil dari mode yang aktif saja — supaya detail ini bisa dipakai laporan.
  const detailTotal = mode === 'campuran'
    ? {
        benar: (hasilDeret?.benar || 0) + (hasilAritmatika?.benar || 0),
        salah: (hasilDeret?.salah || 0) + (hasilAritmatika?.salah || 0),
        kosong: (hasilDeret?.kosong || 0) + (hasilAritmatika?.kosong || 0),
        total: (hasilDeret?.total || 0) + (hasilAritmatika?.total || 0),
      }
    : mode === 'deret' ? hasilDeret
    : mode === 'hitung_cepat' ? hasilHitungCepat
    : hasilAritmatika;

  // Catat hasil tes ke progress tracker (sekali saat hasil ditampilkan)
  useEffect(() => {
    catatHasilTes('logika', skorTotal, { mode, benar: detailTotal?.benar, salah: detailTotal?.salah, kosong: detailTotal?.kosong, total: detailTotal?.total }, tryoutMode, tryoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const labelMode = mode === 'deret' ? '🔢 Deret Angka'
    : mode === 'aritmatika' ? '📖 Aritmatika'
    : mode === 'hitung_cepat' ? '⚡ Hitung Cepat'
    : '🧮 Campuran';

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-2xl mx-auto space-y-4">

        {/* Kartu skor utama */}
        <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
          <div className="text-4xl mb-2">
            {skorTotal >= 75 ? '🏆' : skorTotal >= 60 ? '👍' : '📚'}
          </div>
          <p className="text-xs text-gray-400 mb-1">Mode: {labelMode}</p>
          <h2 className="text-lg font-bold text-gray-800 mb-3">Hasil Tes Logika & Numerik</h2>

          <div className="text-6xl font-extrabold text-blue-600 mb-1">{skorTotal}</div>
          <p className="text-gray-400 text-sm mb-3">Skor Total</p>
          <span className={`inline-block px-4 py-1 rounded-full text-sm font-bold border ${badgeTotal.color}`}>
            {badgeTotal.label}
          </span>

          <div className="w-full bg-gray-200 rounded-full h-3 mt-4 mb-2">
            <div
              className="bg-blue-500 h-3 rounded-full transition-all duration-1000"
              style={{ width: `${skorTotal}%` }}
            />
          </div>
          <p className="text-xs text-gray-400">
            💡 Skor ini akan digabungkan ke nilai akhir psikotes kamu
          </p>
        </div>

        {/* Skor per kategori — tampil jika campuran */}
        {mode === 'campuran' && hasilDeret && hasilAritmatika && (
          <div className="space-y-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide text-center">
              Rincian Skor per Kategori
            </p>
            <KartuSkor
              label="Deret Angka"
              icon="🔢"
              hasil={hasilDeret}
              color="border-purple-200"
            />
            <KartuSkor
              label="Aritmatika"
              icon="📖"
              hasil={hasilAritmatika}
              color="border-green-200"
            />
          </div>
        )}

        {/* Skor single mode */}
        {mode === 'deret' && hasilDeret && (
          <KartuSkor label="Deret Angka" icon="🔢" hasil={hasilDeret} color="border-purple-200" />
        )}
        {mode === 'aritmatika' && hasilAritmatika && (
          <KartuSkor label="Aritmatika" icon="📖" hasil={hasilAritmatika} color="border-green-200" />
        )}
        {mode === 'hitung_cepat' && hasilHitungCepat && (
          <KartuSkor label="Hitung Cepat" icon="⚡" hasil={hasilHitungCepat} color="border-amber-200" />
        )}

        {/* Tombol aksi */}
        <div className="bg-white rounded-2xl shadow p-4 space-y-3">
          <button
            onClick={() => setShowPembahasan(!showPembahasan)}
            className="w-full py-3 rounded-xl border border-blue-300 text-blue-600 font-semibold hover:bg-blue-50 transition"
          >
            {showPembahasan ? 'Sembunyikan' : '📋 Lihat'} Pembahasan
          </button>
          <div className="flex gap-3">
            <button
              onClick={onUlang}
              className="flex-1 py-3 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 transition"
            >
              🔄 Ulangi
            </button>
            <button
              onClick={onBack}
              className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-600 font-semibold hover:bg-gray-50 transition"
            >
              ← Menu
            </button>
          </div>
        </div>

        {/* Pembahasan */}
        {showPembahasan && (
          <div className="space-y-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide text-center">
              Pembahasan Soal
            </p>
            {soalList.map((soal, idx) => {
              const userJawab = jawabanUser.find(j => j.id === soal.id);
              const jwb = userJawab?.jawaban;
              const benar = (soal.jenis === 'deret' || soal.jenis === 'hitung_cepat')
                ? Number(jwb) === Number(soal.jawaban)
                : jwb === soal.jawaban;
              const badgeJenis = soal.jenis === 'deret'
                ? { warna: 'bg-purple-100 text-purple-700', label: '🔢 Deret' }
                : soal.jenis === 'hitung_cepat'
                ? { warna: 'bg-amber-100 text-amber-700', label: '⚡ Hitung Cepat' }
                : { warna: 'bg-green-100 text-green-700', label: '📖 Aritmatika' };

              return (
                <div key={soal.id} className={`bg-white rounded-xl p-4 border-l-4 ${benar ? 'border-green-500' : 'border-red-400'}`}>
                  <div className="flex justify-between items-start mb-2">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${badgeJenis.warna}`}>
                      {idx + 1}. {badgeJenis.label}
                    </span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full
                      ${benar ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {benar ? '✓ Benar' : '✗ Salah'}
                    </span>
                  </div>
                  {soal.jenis === 'deret'
                    ? <p className="text-sm font-semibold text-gray-700 mb-2">{soal.deret}</p>
                    : soal.jenis === 'hitung_cepat'
                    ? <p className="text-sm font-semibold text-gray-700 mb-2">{soal.ekspresi}</p>
                    : <p className="text-sm text-gray-700 mb-2">{soal.soal}</p>
                  }
                  <p className="text-sm">
                    <span className="text-gray-400">Jawabanmu: </span>
                    <span className={`font-bold ${benar ? 'text-green-600' : 'text-red-500'}`}>
                      {jwb !== undefined && jwb !== '' && jwb !== null ? String(jwb).toUpperCase() : '—'}
                    </span>
                    {!benar && (
                      <span className="ml-2 text-gray-400">
                        | Benar: <span className="font-bold text-green-600">
                          {(soal.jenis === 'deret' || soal.jenis === 'hitung_cepat') ? soal.jawaban : String(soal.jawaban).toUpperCase()}
                        </span>
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-400 mt-1 bg-gray-50 rounded-lg px-3 py-2">
                    💡 {soal.pembahasan}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Komponen Utama ───────────────────────────────────────────
export default function LogikaModul({ onBack, tryoutMode = false, tryoutId = null }) {
  const [fase, setFase]           = useState('intro');
  useCegahKeluarTes(fase === 'tes', onBack);
  const [mode, setMode]           = useState(null);
  const [soalList, setSoalList]   = useState([]);
  const [indeks, setIndeks]       = useState(0);
  const [jawabanUser, setJawabanUser] = useState([]);
  const [waktuSisa, setWaktuSisa] = useState(WAKTU_TOTAL);
  const timerRef                  = useRef(null);
  const [progresTersimpan, setProgresTersimpan] = useState(null);

  // ── Cek progres tersimpan sekali di awal (fase intro) — supaya bisa
  // ditawarkan opsi "Lanjutkan" alih-alih mulai dari nol kalau user tadinya
  // keluar tanpa sengaja (koneksi putus, dsb).
  useEffect(() => {
    setProgresTersimpan(bacaProgresTes('logika', tryoutId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useAutosaveTes(fase === 'tes', 'logika', tryoutId, {
    mode,
    soalMeta: soalList.map(s => ({ id: s.id, jenis: s.jenis })),
    indeks,
    jawabanUser,
    waktuSisa,
  });

  // ── Generate soal sesuai mode
  function generateSoal(selectedMode) {
    if (selectedMode === 'deret') {
      return getRandomSoalDeret(TOTAL_SOAL).map(s => ({ ...s, jenis: 'deret' }));
    } else if (selectedMode === 'aritmatika') {
      return getRandomSoalAritmatika(TOTAL_SOAL).map(s => ({ ...s, jenis: 'aritmatika' }));
    } else if (selectedMode === 'hitung_cepat') {
      return getRandomSoalHitungCepat(TOTAL_SOAL).map(s => ({ ...s, jenis: 'hitung_cepat' }));
    } else {
      // Mode campuran (komposisi resmi Tryout): kategori TIDAK diacak silang
      // (dulu di-shuffle penuh sehingga soal antar-kategori bercampur acak dan
      // user gampang bingung). Sekarang tiap kategori dikerjakan berurutan per
      // blok (Deret dulu, Aritmatika, baru Hitung Cepat) — tetap 1 modul/1 alur
      // soal 1..30, cuma bloknya jelas. Lihat juga divider "Mulai bagian ..." di
      // layar soal. 10+10+10 supaya Hitung Cepat (soal baru) ikut ke skor Tryout
      // resmi tanpa menambah total soal/durasi (tetap 30 soal, ~25 menit).
      const deret       = getRandomSoalDeret(10).map(s => ({ ...s, jenis: 'deret' }));
      const aritmatika  = getRandomSoalAritmatika(10).map(s => ({ ...s, jenis: 'aritmatika' }));
      const hitungCepat = getRandomSoalHitungCepat(10).map(s => ({ ...s, jenis: 'hitung_cepat' }));
      return [...deret, ...aritmatika, ...hitungCepat];
    }
  }

  // ── Mulai tes (baru — buang progres lama kalau ada)
  const handleMulai = useCallback((selectedMode) => {
    hapusProgresTes('logika', tryoutId);
    const soal = generateSoal(selectedMode);
    setMode(selectedMode);
    setSoalList(soal);
    setIndeks(0);
    setJawabanUser(soal.map(s => ({ id: s.id, jawaban: null })));
    setWaktuSisa(WAKTU_TOTAL);
    setProgresTersimpan(null);
    setFase('tes');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tryoutId]);

  // ── Lanjutkan dari progres tersimpan (bukan generate soal baru — pakai ID
  // yang sudah tersimpan supaya soal & urutannya persis sama seperti terakhir).
  const handleLanjutkan = useCallback(() => {
    if (!progresTersimpan) return;
    const soal = soalDariMeta(progresTersimpan.soalMeta);
    if (soal.length === 0) { setProgresTersimpan(null); return; }
    setMode(progresTersimpan.mode);
    setSoalList(soal);
    setIndeks(Math.min(progresTersimpan.indeks || 0, soal.length - 1));
    setJawabanUser(progresTersimpan.jawabanUser || soal.map(s => ({ id: s.id, jawaban: null })));
    setWaktuSisa(progresTersimpan.waktuSisa ?? WAKTU_TOTAL);
    setFase('tes');
  }, [progresTersimpan]);

  const handleMulaiBaruDariBanner = useCallback(() => {
    hapusProgresTes('logika', tryoutId);
    setProgresTersimpan(null);
  }, [tryoutId]);

  // ── Timer
  useEffect(() => {
    if (fase !== 'tes') return;
    timerRef.current = setInterval(() => {
      setWaktuSisa(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); hapusProgresTes('logika', tryoutId); setFase('hasil'); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fase]);

  // ── Jawab soal
  const handleJawab = useCallback((nilai) => {
    setJawabanUser(prev =>
      prev.map(j => j.id === soalList[indeks].id ? { ...j, jawaban: nilai } : j)
    );
  }, [indeks, soalList]);

  // ── Navigasi
  const handleNext = useCallback(() => {
    if (indeks < soalList.length - 1) setIndeks(i => i + 1);
    else { clearInterval(timerRef.current); hapusProgresTes('logika', tryoutId); setFase('hasil'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indeks, soalList.length, tryoutId]);

  const handlePrev = useCallback(() => {
    if (indeks > 0) setIndeks(i => i - 1);
  }, [indeks]);

  const handleSelesai = useCallback(() => {
    clearInterval(timerRef.current);
    hapusProgresTes('logika', tryoutId);
    setFase('hasil');
  }, [tryoutId]);

  const handleUlang = useCallback(() => {
    setFase('intro');
    setMode(null);
    setSoalList([]);
    setIndeks(0);
    setJawabanUser([]);
    setWaktuSisa(WAKTU_TOTAL);
  }, []);

  // ── Render
  if (fase === 'intro') {
    return (
      <LayarIntro
        onMulai={handleMulai}
        onBack={onBack}
        tryoutMode={tryoutMode}
        progresTersimpan={progresTersimpan}
        onLanjutkan={handleLanjutkan}
        onMulaiBaru={handleMulaiBaruDariBanner}
      />
    );
  }

  if (fase === 'hasil') {
    return (
      <LayarHasil
        mode={mode}
        tryoutMode={tryoutMode}
        tryoutId={tryoutId}
        soalList={soalList}
        jawabanUser={jawabanUser}
        onUlang={handleUlang}
        onBack={onBack}
      />
    );
  }

  // ── Layar Tes
  const soal = soalList[indeks];
  if (!soal) return null;

  const jawabanSoalIni = jawabanUser.find(j => j.id === soal.id)?.jawaban;
  const progress       = ((indeks + 1) / soalList.length) * 100;
  const warnaMeter     = waktuSisa < 120 ? 'bg-red-500' : waktuSisa < 300 ? 'bg-yellow-400' : 'bg-green-500';

  // Divider "Mulai bagian ..." — hanya di mode campuran, dan hanya di soal
  // pertama tiap blok kategori (kategori beda dari soal sebelumnya).
  const mulaiKategoriBaru = mode === 'campuran' && (indeks === 0 || soalList[indeks - 1]?.jenis !== soal.jenis);
  const infoKategori = soal.jenis === 'deret'
    ? { label: 'Deret Angka', icon: '🔢', jumlah: soalList.filter(s => s.jenis === 'deret').length }
    : soal.jenis === 'hitung_cepat'
    ? { label: 'Hitung Cepat', icon: '⚡', jumlah: soalList.filter(s => s.jenis === 'hitung_cepat').length }
    : { label: 'Aritmatika', icon: '📖', jumlah: soalList.filter(s => s.jenis === 'aritmatika').length };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <div className={`font-mono font-bold text-lg px-3 py-1 rounded-lg
              ${waktuSisa < 120 ? 'text-red-600 bg-red-50' : 'text-gray-700 bg-gray-100'}`}>
              ⏱ {formatWaktu(waktuSisa)}
            </div>
            <span className="text-sm text-gray-500 font-semibold">
              Soal <span className="text-blue-600">{indeks + 1}</span> / {soalList.length}
            </span>
            <span className={`text-xs font-bold px-2 py-1 rounded-full
              ${soal.jenis === 'deret' ? 'bg-purple-100 text-purple-700'
                : soal.jenis === 'hitung_cepat' ? 'bg-amber-100 text-amber-700'
                : 'bg-green-100 text-green-700'}`}>
              {soal.jenis === 'deret' ? '🔢 Deret' : soal.jenis === 'hitung_cepat' ? '⚡ Hitung Cepat' : '📖 Aritmatika'}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className={`h-2 rounded-full transition-all duration-300 ${warnaMeter}`} style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      {/* Soal */}
      <div className="max-w-2xl mx-auto p-4">
        {mulaiKategoriBaru && (
          <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 text-blue-700 rounded-xl px-4 py-2.5 mb-3 text-sm font-semibold">
            <span>{infoKategori.icon}</span>
            <span>Mulai bagian: {infoKategori.label} ({infoKategori.jumlah} soal)</span>
          </div>
        )}
        <div className="bg-white rounded-2xl shadow p-6 mb-4">
          {soal.jenis === 'deret'
            ? <SoalDeret soal={soal} onJawab={handleJawab} jawaban={jawabanSoalIni} />
            : soal.jenis === 'hitung_cepat'
            ? <SoalHitungCepat soal={soal} onJawab={handleJawab} jawaban={jawabanSoalIni} />
            : <SoalAritmatika soal={soal} onJawab={handleJawab} jawaban={jawabanSoalIni} />
          }
        </div>

        {/* Navigasi */}
        <div className="flex gap-3 mb-4">
          <button
            onClick={handlePrev}
            disabled={indeks === 0}
            className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-600 font-semibold hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            ← Sebelumnya
          </button>
          {indeks < soalList.length - 1 ? (
            <button onClick={handleNext} className="flex-grow py-3 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 transition">
              Selanjutnya →
            </button>
          ) : (
            <button onClick={handleSelesai} className="flex-grow py-3 rounded-xl bg-green-600 text-white font-bold hover:bg-green-700 transition">
              Selesai ✓
            </button>
          )}
        </div>

        {/* Navigator soal */}
        <div className="flex flex-wrap gap-1 justify-center">
          {soalList.map((s, i) => {
            const dijawab    = jawabanUser.find(j => j.id === s.id)?.jawaban;
            const sudahDiisi = dijawab !== null && dijawab !== undefined && dijawab !== '';
            return (
              <button
                key={s.id}
                onClick={() => setIndeks(i)}
                className={`w-8 h-8 text-xs rounded-lg font-semibold transition
                  ${i === indeks       ? 'bg-blue-600 text-white'
                  : sudahDiisi        ? 'bg-green-500 text-white'
                  : s.jenis === 'deret' ? 'bg-purple-100 text-purple-600 hover:bg-purple-200'
                  : s.jenis === 'hitung_cepat' ? 'bg-amber-100 text-amber-600 hover:bg-amber-200'
                  : 'bg-green-100 text-green-600 hover:bg-green-200'}`}
              >
                {i + 1}
              </button>
            );
          })}
        </div>
        <p className="text-center text-xs text-gray-400 mt-2">
          🟦 Aktif &nbsp;|&nbsp; 🟩 Sudah dijawab &nbsp;|&nbsp; 🟪 Deret &nbsp;|&nbsp; 🟢 Aritmatika
        </p>
      </div>
    </div>
  );
}
